// The flow's one loading screen: the walk's own loading chrome (so every
// loading moment in the flow is pixel-identical to every other) plus a
// rotating status line so a long wait visibly progresses. The statuses
// are honest, present-tense descriptions of the work actually happening —
// warmth over jokes — rotating on a lazy beat with jitter so two loads
// never feel like a looped animation.

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { FormLoadingScreen } from "components/FormV2";

import { NARROW_MAIN_CLASS } from "components/ScreenChrome";

/** ~3s per line, with enough jitter that the rhythm never reads as a
 * metronome. A single-status screen simply holds its line. */
function useRotatingStatus(statuses: readonly string[]): {
  status: string;
  index: number;
} {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (statuses.length < 2) return;
    let timer: number;
    let current = 0;
    const schedule = () => {
      timer = window.setTimeout(
        () => {
          current = (current + 1) % statuses.length;
          setIndex(current);
          schedule();
        },
        2600 + Math.random() * 1400,
      );
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [statuses]);
  return { status: statuses[index] ?? "", index };
}

export default function LoadingScreen({
  kicker,
  titleA,
  titleB,
  statuses,
  showProgressDots = false,
}: {
  kicker: string;
  titleA: string;
  titleB: string;
  /** Must be referentially stable (a module constant) — a new array
   * identity restarts the rotation. */
  statuses: readonly string[];
  /** One dot per status, the current one lit — for waits with real
   * stages behind them (the sign step's document prep). */
  showProgressDots?: boolean;
}) {
  const { status, index } = useRotatingStatus(statuses);
  return (
    <main className={NARROW_MAIN_CLASS}>
      <FormLoadingScreen
        section={kicker}
        title={
          <>
            {titleA}{" "}
            <span className="font-sans font-bold text-evergreen">{titleB}</span>
          </>
        }
        description={
          <>
            <AnimatePresence mode="wait">
              <motion.p
                key={status}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {status}
              </motion.p>
            </AnimatePresence>
            {showProgressDots && (
              <span className="mt-4 flex justify-center gap-2">
                {statuses.map((label, dot) => (
                  <span
                    key={label}
                    className={
                      dot === index
                        ? "h-1.5 w-1.5 rounded-full bg-evergreen"
                        : "h-1.5 w-1.5 rounded-full bg-oat-300"
                    }
                  />
                ))}
              </span>
            )}
          </>
        }
      />
    </main>
  );
}
